// New-email alerts (Umer, 2026-09-16) — the Email counterpart of the Text
// Inbox popups. Two jobs:
//
// 1. Keep the inbox flowing while NOBODY has the Email page open. The page
//    is the only thing that polls IMAP, so a dispatcher on the Dashboard
//    would otherwise never hear about a customer's reply. The background
//    sync takes the same fleet-wide lock (`emailSyncLock:inbox`) as the
//    page, so N open consoles still add up to one IMAP poll per interval,
//    and it steps aside entirely whenever this browser IS on /inbox.
// 2. Report the newest unread inbox rows to the notification layer, on a
//    slow poll plus a realtime nudge when the table changes (a sync landing
//    rows from any console). The caller decides what is "new" (seen-set).
//
// Supabase only: the Firestore line has neither the table nor the lock.
import type { EmailRecord } from './emailApi';
import { syncFolder, maxCachedUid, shouldBackgroundSync, loadOlderEmails, serverSyncAlive } from './emailCache';
import { autoCreateCardsFromEmails } from './paymentBoard';
import { USE_SUPABASE } from './supabaseClient';
import { FEATURES } from './features';
import { subscribeTableHub } from './tableHub';
import { claimTabLeader } from './tabLeader';

const POLL_MS = 15_000; // matches the Email page's INBOX_POLL_MS
// Same gap the Email page uses for its own inbox poll (INBOX_POLL_MS * 0.8).
const LOCK_GAP_MS = 12_000;
const REALTIME_DEBOUNCE_MS = 1_500;
const HIDDEN_TICK_EVERY = 4; // hidden tab: one backup poll a minute (4 × POLL_MS)
const WATCH_LIMIT = 25;
const MINTED_MAX = 2_000;
const minted = new Set<string>(); // rows already offered to the board (server-sync mode)

// One inbox poll, if the fleet lock says it's our turn. Returns what landed.
// Order cards are minted exactly as the page would (the board gate inside
// autoCreateCardsFromEmails decides whether a site writes any).
export async function backgroundInboxSync(): Promise<EmailRecord[]> {
  if (FEATURES.emailServerSync && (await serverSyncAlive())) return []; // the server pulls the mailbox (email-sync-cron) and is alive
  if (!(await shouldBackgroundSync(LOCK_GAP_MS, 'emailSyncLock:inbox'))) return [];
  const maxUid = await maxCachedUid('inbox').catch(() => 0);
  const fresh = await syncFolder('inbox', [], { maxUid });
  if (fresh.length) await autoCreateCardsFromEmails(fresh).catch(() => []);
  return fresh;
}

export function subscribeNewInboxEmails(opts: {
  /** True while this browser has the Email page open — it polls IMAP itself. */
  isOnInboxPage: () => boolean;
  /** Newest unread inbox rows, every poll; caller diffs against what it has seen. */
  onRows: (rows: EmailRecord[]) => void;
}): () => void {
  if (!USE_SUPABASE) return () => {};
  let stopped = false;
  let running = false;
  // One tab per browser leads (Phase 1, 2026-09-30): it creates the order cards and keeps watching while
  // hidden. Every other tab reads only while it is shown — each new email used to make every open tab
  // read the 25 newest unread rows and try to create the same cards.
  let leader = false;
  let heldWhileHidden = false;
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  let again = false; // a tick asked for while one runs (e.g. the lead granted mid-read) runs right after it
  const tick = async (): Promise<void> => {
    if (stopped) return;
    if (running) { again = true; return; }
    if (hidden() && !leader) { heldWhileHidden = true; return; }
    running = true;
    try {
      if (!opts.isOnInboxPage()) await backgroundInboxSync().catch(() => []);
      const rows = await loadOlderEmails('unread', null, WATCH_LIMIT);
      if (!stopped) opts.onRows(rows);
      // Server-sync mode: the IMAP fetch that used to mint order cards here is
      // gone, so mint from the unread rows instead — each row once per tab,
      // on whatever page is open. Idempotent (one card per order key).
      if (leader && FEATURES.emailServerSync && rows.length) {
        const unseen = rows.filter((r) => !minted.has(`${r.folder}-${r.uid}`));
        if (unseen.length) {
          unseen.forEach((r) => minted.add(`${r.folder}-${r.uid}`));
          if (minted.size > MINTED_MAX) minted.clear(); // bounded; a repeat is a no-op anyway
          await autoCreateCardsFromEmails(unseen).catch(() => []);
        }
      }
    } catch {
      /* a failed poll keeps the last state; never throws into the app shell */
    } finally {
      running = false;
      if (again && !stopped) { again = false; void tick(); }
    }
  };
  // Claimed before the first read: a browser without Web Locks (or a free lock) leads from the start.
  let started = false;
  const stopLeading = claimTabLeader('wolf:email-alerts', () => { leader = true; if (started) void tick(); });
  started = true;
  tick();
  const onVisible = () => { if (!hidden() && heldWhileHidden) { heldWhileHidden = false; void tick(); } };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  // Hidden tab: the timer ticks once a minute instead of every 15 s. New rows
  // still arrive through the realtime nudge below, so a popup for mail that
  // lands while the tab is in the background is not delayed — only the
  // backup poll is (audit, 2026-09-25). The IMAP side already waits
  // HIDDEN_TAB_MIN_GAP_MS when hidden; this trims the 4 list reads a minute
  // that went with it.
  let hiddenTicks = 0;
  const timer = setInterval(() => {
    if (hidden() && ++hiddenTicks % HIDDEN_TICK_EVERY !== 0) return;
    hiddenTicks = 0;
    tick();
  }, POLL_MS);
  let debounce: ReturnType<typeof setTimeout> | null = null;
  // INSERTs only, on the tab's shared `emails` binding (services/tableHub).
  const leave = subscribeTableHub('emails', {
    events: ['INSERT'],
    onChange: () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(tick, REALTIME_DEBOUNCE_MS); // a sync batch is many inserts; one re-read
    },
  });
  return () => {
    stopped = true;
    clearInterval(timer);
    if (debounce) clearTimeout(debounce);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    stopLeading();
    leave();
  };
}
